// The editor's shell: header, tab menu, save bar, publish dialog, preview panel and conflict dialog
const en = {
  backToVersions: 'Back to versions',
  versions: 'Versions',
  loadFailed: 'Failed to load version data',
  backgroundImageMissing: 'Choose a background image, or another background type, before saving.',
  saveFailed: 'An error occurred while saving',
  errorSaving: 'Error saving',
  errorReloading: 'Error reloading',
  savedInstantly: 'Saved instantly, in every version',
  preview: 'Preview',
  hidePreview: 'Hide preview',
  editWithAgent: 'Edit with agent',
  agentDraftRequired: 'Open or create a draft to edit with the agent.',
  saving: 'Saving...',
  /** The more actions ("…") menu button, when its screen gives it no label */
  moreActions: 'More actions',

  unsavedTitle: 'Unsaved changes',
  unsavedQuestion: 'Do you want to save your changes?',
  discardChanges: 'Discard changes',
  saveAndQuit: 'Save & quit',

  menuLabel: 'Editor sections',
  savedWithVersion: (version: number) => `Saved with version ${version}`,
  liveSection: 'Live, saved instantly',
  tabs: {
    infos: 'Info & Layout',
    map: 'Maps',
    tickets: 'Tickets',
    pois: 'Places',
    artists: 'Artists',
    shows: 'Shows',
    history: 'History',
    pages: 'Pages',
    news: 'News',
    notifications: 'Notifications',
    users: 'Users',
    carpools: 'Carpools',
    reporting: 'Reports',
    advertisements: 'Advertisements',
  },

  unsavedCount: (count: number) => `${count} unsaved change${count > 1 ? 's' : ''}`,
  allSavedAt: (time: string) => `All saved at ${time}`,
  draft: 'Draft',
  published: 'Published',
  backToDraft: 'Back to draft…',
  saveChanges: 'Save Changes',
  publish: 'Publish…',

  checkingLiveVersion: 'Checking which version the apps show…',
  liveVersionUnknown: 'The apps show the highest-numbered published version.',
  newerStaysLive: (newer: number | null, version: number) =>
    `Version ${newer} is newer and stays live, so the apps keep showing it. ` +
    `Version ${version} only shows if version ${newer} goes back to draft.`,
  appsStartShowing: (version: number) => `The apps will start showing version ${version}.`,
  appsShowInPlaceOf: (version: number, before: number) =>
    `The apps will show version ${version} in place of version ${before}.`,
  appsKeepShowing: (version: number | null) => `The apps keep showing version ${version}.`,
  appsLeftEmpty: 'No other version is published, so the apps will have nothing to show.',
  appsGoBackTo: (version: number) => `The apps will go back to version ${version}.`,
  publishQuestion: (version: number) => `Publish version ${version}?`,
  unpublishQuestion: (version: number) => `Move version ${version} back to draft?`,
  unsavedSavedWithIt: (count: number) => `Your ${count} unsaved change${count > 1 ? 's are' : ' is'} saved with it.`,
  publishVersion: (version: number) => `Publish version ${version}`,
  moveToDraft: 'Move to draft',

  previewTitle: (version: number) => `App preview · version ${version}`,
  refreshHint: 'Reload the saved data, e.g. after editing news or pages',
  refresh: 'Refresh',
  closePreview: 'Close preview',
  previewUnsaved: 'Unsaved changes aren\'t in the preview yet. Save to see them.',
  appPreview: 'App preview',

  conflictTitle: 'Update Conflict Detected',
  conflictNewerVersion: 'A newer version exists on the server.',
  conflictChanges: 'Changes made by others:',
  overwrite: 'Overwrite with these changes',
  reloadFromServer: 'Reload from server',
};

const fr: typeof en = {
  backToVersions: 'Retour aux versions',
  versions: 'Versions',
  loadFailed: 'Impossible de charger les données de la version',
  backgroundImageMissing: 'Choisissez une image d\'arrière-plan, ou un autre type d\'arrière-plan, avant d\'enregistrer.',
  saveFailed: 'Une erreur est survenue lors de l\'enregistrement',
  errorSaving: 'Erreur lors de l\'enregistrement',
  errorReloading: 'Erreur lors du rechargement',
  savedInstantly: 'Enregistré immédiatement, dans toutes les versions',
  preview: 'Aperçu',
  hidePreview: 'Masquer l\'aperçu',
  editWithAgent: 'Modifier avec l’agent',
  agentDraftRequired: 'Ouvrez ou créez un brouillon pour le modifier avec l’agent.',
  saving: 'Enregistrement...',
  moreActions: 'Plus d\'actions',

  unsavedTitle: 'Modifications non enregistrées',
  unsavedQuestion: 'Voulez-vous enregistrer vos modifications\u00a0?',
  discardChanges: 'Abandonner les modifications',
  saveAndQuit: 'Enregistrer et quitter',

  menuLabel: 'Sections de l\'éditeur',
  savedWithVersion: (version: number) => `Dans la version ${version}`,
  liveSection: 'En ligne aussitôt',
  tabs: {
    infos: 'Infos & mise en page',
    map: 'Plans',
    tickets: 'Billetterie',
    pois: 'Lieux',
    artists: 'Artistes',
    shows: 'Shows',
    history: 'Historique',
    pages: 'Pages',
    news: 'Actus',
    notifications: 'Notifications',
    users: 'Utilisateurs',
    carpools: 'Covoiturages',
    reporting: 'Signalements',
    advertisements: 'Publicités',
  },

  unsavedCount: (count: number) => {
    const s = count > 1 ? 's' : '';
    return `${count} modification${s} non enregistrée${s}`;
  },
  allSavedAt: (time: string) => `Tout est enregistré à ${time}`,
  draft: 'Brouillon',
  published: 'Publiée',
  backToDraft: 'Repasser en brouillon…',
  saveChanges: 'Enregistrer les modifications',
  publish: 'Publier…',

  checkingLiveVersion: 'Vérification de la version affichée dans les apps…',
  liveVersionUnknown: 'Les apps affichent la version publiée qui a le numéro le plus élevé.',
  newerStaysLive: (newer: number | null, version: number) =>
    `La version ${newer} est plus récente et reste en ligne, les apps continuent donc de l'afficher. ` +
    `La version ${version} ne s'affichera que si la version ${newer} repasse en brouillon.`,
  appsStartShowing: (version: number) => `Les apps afficheront désormais la version ${version}.`,
  appsShowInPlaceOf: (version: number, before: number) =>
    `Les apps afficheront la version ${version} à la place de la version ${before}.`,
  appsKeepShowing: (version: number | null) => `Les apps continuent d'afficher la version ${version}.`,
  appsLeftEmpty: 'Aucune autre version n\'est publiée\u00a0: les apps n\'auront plus rien à afficher.',
  appsGoBackTo: (version: number) => `Les apps reviendront à la version ${version}.`,
  publishQuestion: (version: number) => `Publier la version ${version}\u00a0?`,
  unpublishQuestion: (version: number) => `Repasser la version ${version} en brouillon\u00a0?`,
  unsavedSavedWithIt: (count: number) => count > 1
    ? `Vos ${count} modifications non enregistrées seront enregistrées en même temps.`
    : 'Votre modification non enregistrée sera enregistrée en même temps.',
  publishVersion: (version: number) => `Publier la version ${version}`,
  moveToDraft: 'Repasser en brouillon',

  previewTitle: (version: number) => `Aperçu de l'app · version ${version}`,
  refreshHint: 'Recharger les données enregistrées, par exemple après avoir modifié des actus ou des pages',
  refresh: 'Actualiser',
  closePreview: 'Fermer l\'aperçu',
  previewUnsaved: 'Les modifications non enregistrées ne sont pas encore dans l\'aperçu. Enregistrez pour les voir.',
  appPreview: 'Aperçu de l\'app',

  conflictTitle: 'Conflit de mise à jour détecté',
  conflictNewerVersion: 'Une version plus récente existe sur le serveur.',
  conflictChanges: 'Modifications faites par d\'autres\u00a0:',
  overwrite: 'Écraser avec mes modifications',
  reloadFromServer: 'Recharger depuis le serveur',
};

const messages = { en, fr };
export default messages;
